import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Typography } from '@sequelae/lore-core';

export type NavigationMode = 'legacy' | 'native';

interface NavigationModeContextType {
  mode: NavigationMode;
  setMode: (mode: NavigationMode) => void;
  isNativeMode: boolean;
  toggleMode: () => void;
}

const NavigationModeContext = createContext<NavigationModeContextType | undefined>(undefined);

export const NavigationModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize from localStorage or default to 'native'
  const [mode, setModeState] = useState<NavigationMode>(() => {
    const stored = localStorage.getItem('navigationMode');
    return (stored === 'legacy' || stored === 'native' ? stored : 'native') as NavigationMode;
  });

  const setMode = useCallback((newMode: NavigationMode) => {
    setModeState(newMode);
    localStorage.setItem('navigationMode', newMode);
    console.log(`[NavigationMode] Switched to ${newMode} mode`);
  }, []);

  const toggleMode = useCallback(() => {
    const newMode = mode === 'legacy' ? 'native' : 'legacy';
    setMode(newMode);
  }, [mode, setMode]);

  const isNativeMode = mode === 'native';

  return (
    <NavigationModeContext.Provider value={{ mode, setMode, isNativeMode, toggleMode }}>
      {children}
    </NavigationModeContext.Provider>
  );
};

export const useNavigationMode = (): NavigationModeContextType => {
  const context = useContext(NavigationModeContext);
  if (!context) {
    throw new Error('useNavigationMode must be used within NavigationModeProvider');
  }
  return context;
};
